import {
	Alert, Box, Button, Grid, Paper, Snackbar, Stack, Tab, Tabs, Tooltip, Typography
} from "@mui/material"
import { useEffect, useState } from "react"
import EditIcon     from '@mui/icons-material/Edit'
import DeleteIcon   from '@mui/icons-material/Delete'
import LanguageIcon from '@mui/icons-material/Language'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import useInternalFetch           from "../../../api/apiInternal"
import Loading                    from "../../../components/Loading"
import GeneralError               from "../../../components/GeneralError"
import config                     from "../../../common/Config"
import { filterPublicFields }     from "../../../common/Fields"

import ContextDialog  from "./components/ContextDialog"
import ConfirmDialog  from "../../../components/ConfirmDialog"
import RiskPanel     from "./components/RiskPanel"
import GrowthPanel   from "./components/GrowthPanel"
import ApprovalPanel from "./components/ApprovalPanel"

const GLOBAL = '__global__'

const UserSettings2 = () => {
	const internalFetch             = useInternalFetch()
	const [fields, setFields]       = useState(null)
	const [contexts, setContexts]   = useState([])
	const [tabValue, setTabValue]   = useState(GLOBAL)
	const [isLoading, setIsLoading] = useState(true)
	const [hasError, setHasError]   = useState(false)
	const [deleteError, setDeleteError]         = useState(null)
	const [dialogOpen, setDialogOpen]           = useState(false)
	const [editingContext, setEditingContext]   = useState(null)
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

	useEffect(() => {
		let isMounted = true
		Promise.all([
			internalFetch(config.apiUrl + '/api/screener/fields'),
			internalFetch(config.apiUrl + '/api/settings/contexts'),
		]).then(([fieldsData, contextsData]) => {
			if (!isMounted) return
			if (fieldsData.status || !Array.isArray(contextsData)) {
				setHasError(true)
			} else {
				setFields(
					filterPublicFields(fieldsData).sort((a, b) =>
						a.categoryOrder !== b.categoryOrder
							? a.categoryOrder - b.categoryOrder
							: a.label.localeCompare(b.label)
					)
				)
				setContexts(contextsData)
			}
			setIsLoading(false)
		}).catch(() => {
			if (!isMounted) return
			setHasError(true)
			setIsLoading(false)
		})
		return () => { isMounted = false }
	}, [])

	const selectedContextId = tabValue === GLOBAL ? null : tabValue
	const currentContext    = contexts.find(c => c.id === tabValue) ?? null

	const openNewDialog  = () => { setEditingContext(null);           setDialogOpen(true) }
	const openEditDialog = () => { setEditingContext(currentContext); setDialogOpen(true) }

	const handleDialogSaved = (saved) => {
		setContexts(prev => {
			const idx = prev.findIndex(c => c.id === saved.id)
			if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next }
			return [...prev, saved]
		})
		setTabValue(saved.id)
		setDialogOpen(false)
	}

	const handleDeleteContext = () => {
		setConfirmDeleteOpen(false)
		internalFetch(config.apiUrl + `/api/settings/contexts/${tabValue}`, { method: 'DELETE' })
			.then(result => {
				if (result?.status) { setDeleteError('Erro ao excluir contexto.'); return }
				setContexts(prev => prev.filter(c => c.id !== tabValue))
				setTabValue(GLOBAL)
			})
	}

	if (isLoading) return <Loading />
	if (hasError)  return <GeneralError message="Erro ao carregar preferências" />

	return (
		<Box sx={{ p: 2 }}>
			<Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>

				{/* Tabs + aba de novo contexto */}
				<Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
					<Tabs
						value={tabValue}
						onChange={(_, v) => { if (v === '__new__') { openNewDialog(); return } setTabValue(v) }}
						variant="standard"
					>
						<Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LanguageIcon sx={{ fontSize: 16 }} />Global</Box>} value={GLOBAL} />
						{contexts.map(ctx => (
							<Tab key={ctx.id} value={ctx.id} label={
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									{ctx.name}
									{ctx.description && (
										<Tooltip title={ctx.description} arrow>
											<InfoOutlinedIcon sx={{ fontSize: 14, color: tabValue === ctx.id ? 'primary.main' : 'text.secondary' }} />
										</Tooltip>
									)}
								</Box>
							} />
						))}
						<Tab label="+ Novo contexto" value="__new__" sx={{ color: 'primary.main' }} />
					</Tabs>
				</Box>

				<Box sx={{ p: 1.5 }}>
					{/* Título + ações do contexto */}
					<Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
						<Typography variant="h6" sx={{ flex: 1 }}>
							Parâmetros de Classificação
						</Typography>
						{selectedContextId && (
							<Stack direction="row" spacing={1}>
								<Button size="small" startIcon={<EditIcon />} onClick={openEditDialog}>
									Editar contexto
								</Button>
								<Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDeleteOpen(true)}>
									Excluir
								</Button>
							</Stack>
						)}
					</Box>

					{/* Painéis */}
					<Grid container spacing={2}>
						<Grid item xs={12} xl={5}>
							<RiskPanel fields={fields} contextId={selectedContextId} />
						</Grid>
						<Grid item xs={12} md={6} xl={4} sx={{ display: 'flex' }}>
							<GrowthPanel fields={fields} contextId={selectedContextId} />
						</Grid>
						<Grid item xs={12} md={6} xl={3} sx={{ display: 'flex' }}>
							<ApprovalPanel contextId={selectedContextId} />
						</Grid>
					</Grid>
				</Box>
			</Paper>

			<ContextDialog
				open={dialogOpen}
				context={editingContext}
				onClose={() => setDialogOpen(false)}
				onSaved={handleDialogSaved}
			/>

			<ConfirmDialog
				open={confirmDeleteOpen}
				setOpen={setConfirmDeleteOpen}
				title="Excluir contexto"
				onConfirm={handleDeleteContext}
			>
				Tem certeza que deseja excluir o contexto <strong>{currentContext?.name}</strong>? Esta ação não pode ser desfeita.
			</ConfirmDialog>

			<Snackbar open={!!deleteError} autoHideDuration={4000} onClose={() => setDeleteError(null)}>
				<Alert severity="error" onClose={() => setDeleteError(null)}>{deleteError}</Alert>
			</Snackbar>
		</Box>
	)
}

export default UserSettings2
